<template>
	<section class="section-card" v-loading="loading">
		<div class="section-head">
			<h2 class="section-title">实时运行数据</h2>
			<div class="section-head__meta">数据更新时间：{{ lastUpdate }}</div>
		</div>

		<div v-if="!loading && !realtimePanels.length" class="realtime-empty">
			<gc-empty />
		</div>
		<div v-else class="device-grid">
			<article v-for="panel in realtimePanels" :key="panel.title" :class="['device-panel', panel.layout]">
				<div class="device-panel__header">
					<div class="device-panel__title">{{ panel.title }}</div>
					<el-button type="text" class="collapse-button" @click="togglePanel(panel.title)">
						{{ isCollapsed(panel.title) ? '展开' : '收起' }}
						<i :class="isCollapsed(panel.title) ? 'el-icon-arrow-down' : 'el-icon-arrow-up'"></i>
					</el-button>
				</div>

				<div v-show="!isCollapsed(panel.title)" class="device-panel__body">
					<div v-for="metric in panel.metrics" :key="metric.label" class="device-metric">
						<div class="device-metric__label">{{ metric.label }}</div>
						<div :class="['device-metric__value', 'device-metric__value--' + metric.status]">
							{{ metric.value }}
						</div>
					</div>
				</div>
			</article>
		</div>
	</section>
</template>

<script>
import { apiGetPressureRegulatorRuntimeRecords } from '@/apis/pressureRegulatorManage.api'

export default {
	name: 'RealtimeDataSection',
	props: {
		collectorDeviceNo: {
			type: [String, Number],
			default: '',
		},
		lastUpdate: {
			type: String,
			default: '',
		},
	},
	data() {
		return {
			loading: false,
			realtimePanels: [],
			collapsedPanels: [],
		}
	},
	watch: {
		collectorDeviceNo: {
			handler(value) {
				if (value) {
					this.getRuntimeRecords()
				} else {
					this.realtimePanels = []
				}
			},
			immediate: true,
		},
		realtimePanels(list) {
			this.collapsedPanels = (list || []).map(panel => panel.title)
		},
	},
	methods: {
		async getRuntimeRecords() {
			this.loading = true
			try {
				const runtimeRecords = await apiGetPressureRegulatorRuntimeRecords({
					collectorDeviceNo: this.collectorDeviceNo,
				})
				this.realtimePanels = (runtimeRecords || []).map((record, index) => ({
					title: this.formatRealtimePanelTitle(record, index),
					layout: '',
					metrics: this.getRuntimeRecordMetrics(record),
				}))
			} finally {
				this.loading = false
			}
		},
		isCollapsed(title) {
			return this.collapsedPanels.includes(title)
		},
		togglePanel(title) {
			if (this.isCollapsed(title)) {
				this.collapsedPanels = this.collapsedPanels.filter(item => item !== title)
			} else {
				this.collapsedPanels = [...this.collapsedPanels, title]
			}
		},
		getRuntimeRecordMetrics(record) {
			const records = record.statusRecords || []
			if (!records.length) {
				return [
					{
						label: '数据时间',
						value: this.formatValue(record.queryTime),
						status: record.queryTime ? 'success' : 'warning',
					},
				]
			}
			return records.map(item => ({
				label: item.name || item.attributeKey || '--',
				value: this.joinValueWithUnit(item.value, item.unit),
				status: this.getMetricStatus(item),
			}))
		},
		formatRealtimePanelTitle(record, index) {
			const deviceTypeName = this.formatValue(record.deviceTypeName)
			const deviceNo = this.formatValue(record.deviceNo || `子设备${index + 1}`)
			return `${deviceTypeName} / ${deviceNo}`
		},
		getMetricStatus(item) {
			return item.value === null || item.value === undefined || item.value === '' ? 'warning' : 'success'
		},
		joinValueWithUnit(value, unit) {
			if (value === null || value === undefined || value === '') return '--'
			return `${value}${unit ? ` ${unit}` : ''}`
		},
		formatValue(value) {
			return value === null || value === undefined || value === '' ? '--' : value
		},
	},
}
</script>

<style lang="scss" scoped>
.section-card {
	position: relative;
	padding: 22px 24px 24px;
	background: var(--panel-bg);
	border: 1px solid var(--panel-border);
	border-radius: 12px;
	box-shadow: var(--panel-shadow);
	backdrop-filter: blur(12px);
}

.section-head {
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 12px;
	margin-bottom: 18px;
}

.section-title {
	margin: 0;
	color: var(--title);
	font-size: 17px;
	font-weight: 700;
	letter-spacing: 0.01em;
}

.section-head__meta {
	font-size: 13px;
	color: var(--muted);
}

.device-grid {
	display: grid;
	grid-template-columns: 1fr;
	gap: 12px;
}

.realtime-empty {
	min-height: 180px;
	display: flex;
	align-items: center;
	justify-content: center;
	padding: 32px 0;
}

.realtime-empty ::v-deep .gc-blank-page {
	margin-top: 0;
}

.device-panel {
	padding: 22px 24px;
	border: 1px solid #e4eaf1;
	border-radius: 12px;
	background: linear-gradient(180deg, #fbfdff 0%, #f8fafc 100%);
	display: flex;
	flex-direction: column;
	gap: 14px;
}

.device-panel__header {
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 12px;
}

.device-panel__body {
	display: flex;
	flex-direction: column;
	gap: 14px;
}

.device-panel__title {
	font-size: 15px;
	color: var(--title);
}

.collapse-button {
	flex-shrink: 0;
	padding: 0;
	font-size: 13px;
	color: var(--primary);
}

.collapse-button i {
	margin-left: 4px;
}

.device-metric {
	display: grid;
	grid-template-columns: minmax(120px, 220px) minmax(110px, 1fr);
	align-items: center;
	gap: 12px;
	padding: 10px 0;
	border-top: 0;
}

.device-metric:first-of-type {
	padding-top: 10px;
}

.device-metric__label {
	font-size: 13px;
	color: var(--muted);
}

.device-metric__value {
	font-size: 16px;
	font-weight: 400;
	text-align: right;
	letter-spacing: 0.02em;
}

.device-metric__value--success {
	color: var(--success);
}

.device-metric__value--warning {
	color: var(--warning);
}

@media (max-width: 900px) {
	.section-head {
		flex-direction: column;
		align-items: flex-start;
	}

	.device-metric {
		grid-template-columns: 1fr;
	}

	.device-metric__value {
		text-align: left;
	}
}
</style>
